import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Gauge, LayoutGrid, CalendarCheck, Ban, Mail, FileText, Car, QrCode, Star,
  DollarSign, Users, CalendarDays, ShieldAlert, BarChart3, UserCog, Receipt,
  Gift, Wrench, Bell, Power, Calculator, Upload, Camera, ClipboardList, BookOpen,
  Cpu, RefreshCw, LogOut, Search, ArrowLeft, Globe, Plus, Trash2, Loader2, X,
  Pencil, UploadCloud, Eye, EyeOff, Lock, KeyRound, CalendarOff,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminLogout, fetchMe, fetchOverview, fetchBookings, cancelBooking, reinstateBooking,
  fetchCustomers, fetchTraffic, fetchAdminCars, createCar, updateCar, deleteCar, uploadImage,
  changePassword, fetchCarSchedule, addBlock, deleteBlock,
} from "../lib/adminApi";
import { imgUrl } from "../lib/img";

const CY = "#22D3EE";
const YL = "#FFD400";

const fmtMoney = (n) => "$" + Number(n || 0).toLocaleString();
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

export default function Admin() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("command");
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const [m, o] = await Promise.all([fetchMe(), fetchOverview()]);
      setMe(m); setOverview(o);
    } catch {
      navigate("/admin/login");
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const logout = async () => { await adminLogout(); navigate("/admin/login"); };
  const refresh = () => { load(); toast.success("Refreshed"); };

  const TABS = useMemo(() => [
    { key: "command", label: "Command Center", icon: Gauge },
    { key: "dashboard", label: "Dashboard", icon: LayoutGrid },
    { key: "bookings", label: "Bookings", icon: CalendarCheck, count: overview?.bookings },
    { key: "cancelled", label: "Cancelled", icon: Ban, count: overview?.cancelled },
    { key: "messages", label: "Messages", icon: Mail, count: 0, soon: true },
    { key: "agreements", label: "Agreements", icon: FileText, count: overview?.agreements },
    { key: "fleet", label: "Fleet", icon: Car, count: overview?.fleet_size },
    { key: "share", label: "Share / QR", icon: QrCode, soon: true },
    { key: "reviews", label: "Reviews", icon: Star, soon: true },
    { key: "money", label: "Money", icon: DollarSign },
    { key: "customers", label: "Customers", icon: Users, count: overview?.customers },
    { key: "availability", label: "Availability", icon: CalendarDays },
    { key: "recovery", label: "Recovery", icon: ShieldAlert, soon: true },
    { key: "traffic", label: "Traffic", icon: BarChart3 },
    { key: "drivers", label: "Drivers", icon: UserCog, soon: true },
    { key: "citations", label: "Citations", icon: Receipt, soon: true },
    { key: "rewards", label: "Rewards", icon: Gift, soon: true },
    { key: "maintenance", label: "Maintenance", icon: Wrench, soon: true },
    { key: "notifications", label: "Notifications", icon: Bell, soon: true },
    { key: "automations", label: "Automations", icon: Power, soon: true },
    { key: "tax", label: "Tax / P&L", icon: Calculator, soon: true },
    { key: "import", label: "Import", icon: Upload, soon: true },
    { key: "inspections", label: "Inspections", icon: Camera, soon: true },
    { key: "playbook", label: "Playbook", icon: ClipboardList, soon: true },
    { key: "help", label: "Help", icon: BookOpen, soon: true },
    { key: "system", label: "System", icon: Cpu },
  ], [overview]);

  const visibleTabs = TABS.filter((t) => t.label.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="admin-page" className="min-h-screen bg-black text-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button onClick={() => navigate("/")} className="flex items-center gap-3 text-left" data-testid="admin-home-link">
            <ArrowLeft size={20} className="text-white/50" />
            <span className="font-serif text-2xl">A+Auto <span className="text-[#FFD400]">Fleet</span></span>
            <span className="text-white/40">·</span>
            <span className="text-white/70 tracking-wide">Admin</span>
          </button>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh-btn" onClick={refresh} className="flex items-center gap-2 border border-white/15 rounded-lg px-4 py-2 text-sm hover:border-white/40 transition-colors">
              <RefreshCw size={15} /> Refresh
            </button>
            <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 border border-white/15 rounded-lg px-4 py-2 text-sm hover:border-red-400/50 hover:text-red-300 transition-colors">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-8">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="admin-search-input"
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder='Search admin… (try "bookings", "traffic", "money")'
            className="w-full bg-[#0B0B0B] border border-white/12 rounded-xl pl-11 pr-4 py-3.5 text-sm focus:outline-none focus:border-[#FFD400]/60"
          />
        </div>

        {/* Tab grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
          {visibleTabs.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`admin-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2.5 rounded-xl border px-4 py-3.5 text-sm transition-all ${
                  active ? "bg-[#FFD400] text-black border-[#FFD400] font-medium"
                  : "bg-[#0B0B0B] border-white/10 text-white/80 hover:border-white/30"
                }`}
              >
                <t.icon size={16} className={active ? "text-black" : "text-[#22D3EE]"} />
                <span className="truncate">{t.label}</span>
                {t.count != null && (
                  <span className={`ml-auto text-xs ${active ? "text-black/70" : "text-white/50"}`}>({t.count})</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Panel */}
        <TabPanel tab={tab} overview={overview} onChanged={load} />
      </div>
    </div>
  );
}

/* ---------- Reusable ---------- */
const InfoBanner = ({ title }) => (
  <div className="border border-[#FFD400]/30 bg-[#FFD400]/5 rounded-xl px-5 py-4 mb-8 flex items-center gap-3">
    <BookOpen size={16} className="text-[#FFD400]" />
    <span className="text-sm"><span className="text-[#FFD400] font-medium">How this tab works</span> <span className="text-white/50">· {title}</span></span>
  </div>
);

const Stat = ({ label, value, icon: Icon, color = "#FFFFFF", testid }) => (
  <div data-testid={testid} className="border border-white/10 bg-[#0B0B0B] rounded-2xl p-6">
    <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-wider">
      {Icon && <Icon size={14} />} {label}
    </div>
    <p className="text-4xl font-semibold mt-3" style={{ color }}>{value}</p>
  </div>
);

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 mb-6">
    {Icon && <Icon size={22} className="text-[#22D3EE]" />}
    <h2 className="font-serif text-3xl">{children}</h2>
  </div>
);

const StatusPill = ({ status }) => (
  <span className={`text-[11px] uppercase tracking-wide px-2.5 py-1 rounded-full border ${
    status === "confirmed" ? "border-emerald-400/40 text-emerald-300 bg-emerald-400/10"
    : "border-red-400/40 text-red-300 bg-red-400/10"
  }`}>{status}</span>
);

/* ---------- Panels ---------- */
function TabPanel({ tab, overview, onChanged }) {
  if (tab === "command" || tab === "dashboard") return <DashboardPanel overview={overview} />;
  if (tab === "bookings") return <BookingsPanel status="confirmed" onChanged={onChanged} title="Active Bookings" />;
  if (tab === "cancelled") return <BookingsPanel status="cancelled" onChanged={onChanged} title="Cancelled Bookings" />;
  if (tab === "agreements") return <AgreementsPanel />;
  if (tab === "fleet") return <FleetPanel onChanged={onChanged} />;
  if (tab === "customers") return <CustomersPanel />;
  if (tab === "money") return <MoneyPanel overview={overview} />;
  if (tab === "traffic") return <TrafficPanel />;
  if (tab === "availability") return <AvailabilityPanel />;
  if (tab === "system") return <SystemPanel />;
  return <ComingSoon tab={tab} />;
}

function DashboardPanel({ overview }) {
  if (!overview) return <Loading />;
  return (
    <div data-testid="panel-dashboard">
      <InfoBanner title="Command Center overview" />
      <SectionTitle icon={Gauge}>Command Center</SectionTitle>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat testid="stat-revenue" label="Revenue" value={fmtMoney(overview.revenue)} icon={DollarSign} color={YL} />
        <Stat testid="stat-active" label="Active Bookings" value={overview.bookings} icon={CalendarCheck} color={CY} />
        <Stat testid="stat-cancelled" label="Cancelled" value={overview.cancelled} icon={Ban} />
        <Stat testid="stat-customers" label="Customers" value={overview.customers} icon={Users} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Stat label="Fleet Size" value={overview.fleet_size} icon={Car} />
        <Stat label="Agreements" value={overview.agreements} icon={FileText} />
        <Stat label="Site Visits" value={overview.total_visits} icon={BarChart3} color={CY} />
        <Stat label="Avg / Booking" value={overview.bookings ? fmtMoney(Math.round(overview.revenue / overview.bookings)) : "$0"} icon={DollarSign} color={YL} />
      </div>

      <h3 className="font-serif text-2xl mb-4">Recent Bookings</h3>
      {overview.recent.length === 0 ? <Empty text="No bookings yet." /> : (
        <div className="border border-white/10 rounded-2xl overflow-hidden">
          {overview.recent.map((b) => (
            <div key={b.reference} className="flex items-center justify-between px-5 py-4 border-b border-white/5 last:border-0">
              <div>
                <p className="font-medium">{b.first_name} {b.last_name}</p>
                <p className="text-white/40 text-sm">{b.car_name} · {b.reference}</p>
              </div>
              <div className="text-right flex items-center gap-4">
                <span className="text-[#FFD400]">{fmtMoney(b.total_price)}</span>
                <StatusPill status={b.status} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BookingsPanel({ status, title, onChanged }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => fetchBookings(status).then(setRows);
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  const doCancel = async (ref) => {
    setBusy(ref);
    try { await cancelBooking(ref); toast.success(`${ref} cancelled`); await load(); onChanged?.(); }
    catch { toast.error("Failed to cancel"); } finally { setBusy(""); }
  };
  const doReinstate = async (ref) => {
    setBusy(ref);
    try { await reinstateBooking(ref); toast.success(`${ref} reinstated`); await load(); onChanged?.(); }
    catch { toast.error("Failed"); } finally { setBusy(""); }
  };

  if (!rows) return <Loading />;
  return (
    <div data-testid={`panel-${status}`}>
      <InfoBanner title={`${title} — manage reservations`} />
      <SectionTitle icon={status === "cancelled" ? Ban : CalendarCheck}>{title}</SectionTitle>
      {rows.length === 0 ? <Empty text="Nothing here yet." /> : (
        <div className="space-y-3">
          {rows.map((b) => (
            <div key={b.reference} data-testid={`booking-row-${b.reference}`} className="border border-white/10 bg-[#0B0B0B] rounded-xl p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <p className="font-medium text-lg">{b.first_name} {b.last_name}</p>
                    <StatusPill status={b.status} />
                  </div>
                  <p className="text-white/50 text-sm mt-1">{b.email} · {b.phone}</p>
                  <p className="text-white/40 text-sm mt-2">
                    {b.car_name} · {b.reference} · {b.pickup_date} → {b.dropoff_date} ({b.days}d)
                  </p>
                  <p className="text-white/40 text-sm">Pickup: {b.pickup_location}</p>
                </div>
                <div className="text-right">
                  <p className="text-[#FFD400] text-xl">{fmtMoney(b.total_price)}</p>
                  <p className="text-white/30 text-xs">pay at pickup</p>
                  {b.status === "confirmed" ? (
                    <button data-testid={`cancel-btn-${b.reference}`} onClick={() => doCancel(b.reference)} disabled={busy === b.reference}
                      className="mt-3 text-sm border border-red-400/40 text-red-300 rounded-lg px-4 py-1.5 hover:bg-red-400/10 disabled:opacity-50">
                      {busy === b.reference ? "…" : "Cancel"}
                    </button>
                  ) : (
                    <button data-testid={`reinstate-btn-${b.reference}`} onClick={() => doReinstate(b.reference)} disabled={busy === b.reference}
                      className="mt-3 text-sm border border-emerald-400/40 text-emerald-300 rounded-lg px-4 py-1.5 hover:bg-emerald-400/10 disabled:opacity-50">
                      {busy === b.reference ? "…" : "Reinstate"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AgreementsPanel() {
  const [rows, setRows] = useState(null);
  useEffect(() => { fetchBookings("all").then(setRows); }, []);
  if (!rows) return <Loading />;
  const signed = rows.filter((r) => r.agreed_terms);
  return (
    <div data-testid="panel-agreements">
      <InfoBanner title="Signed rental agreements" />
      <SectionTitle icon={FileText}>Signed Agreements</SectionTitle>
      {signed.length === 0 ? <Empty text="No signed agreements yet." /> : (
        <div className="border border-white/10 rounded-2xl overflow-hidden">
          {signed.map((b) => (
            <div key={b.reference} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-white/5 last:border-0">
              <div>
                <p className="font-medium">{b.first_name} {b.last_name}</p>
                <p className="text-white/40 text-sm">{b.reference} · {b.car_name}</p>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-emerald-300 text-sm flex items-center gap-1.5"><FileText size={14} /> Signed {fmtDate(b.created_at)}</span>
                <StatusPill status={b.status} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CustomersPanel() {
  const [rows, setRows] = useState(null);
  useEffect(() => { fetchCustomers().then(setRows); }, []);
  if (!rows) return <Loading />;
  return (
    <div data-testid="panel-customers">
      <InfoBanner title="Everyone who has booked with you" />
      <SectionTitle icon={Users}>Customers</SectionTitle>
      {rows.length === 0 ? <Empty text="No customers yet." /> : (
        <div className="border border-white/10 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-12 px-5 py-3 text-white/40 text-xs uppercase tracking-wider border-b border-white/10">
            <span className="col-span-4">Customer</span><span className="col-span-3">Phone</span>
            <span className="col-span-2">Bookings</span><span className="col-span-3 text-right">Total Spend</span>
          </div>
          {rows.map((c) => (
            <div key={c.email} className="grid grid-cols-12 px-5 py-4 border-b border-white/5 last:border-0 items-center">
              <div className="col-span-4"><p className="font-medium">{c.name}</p><p className="text-white/40 text-sm">{c.email}</p></div>
              <span className="col-span-3 text-white/60 text-sm">{c.phone}</span>
              <span className="col-span-2 text-white/60">{c.bookings}</span>
              <span className="col-span-3 text-right text-[#FFD400]">{fmtMoney(c.total_spend)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MoneyPanel({ overview }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { fetchBookings("confirmed").then(setRows); }, []);
  const byCar = useMemo(() => {
    if (!rows) return [];
    const m = {};
    rows.forEach((b) => { m[b.car_name] = (m[b.car_name] || 0) + b.total_price; });
    return Object.entries(m).map(([car, total]) => ({ car, total })).sort((a, b) => b.total - a.total);
  }, [rows]);
  if (!overview || !rows) return <Loading />;
  const max = Math.max(1, ...byCar.map((x) => x.total));
  return (
    <div data-testid="panel-money">
      <InfoBanner title="Revenue from confirmed bookings" />
      <SectionTitle icon={DollarSign}>Money</SectionTitle>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        <Stat label="Total Revenue" value={fmtMoney(overview.revenue)} icon={DollarSign} color={YL} />
        <Stat label="Confirmed Bookings" value={overview.bookings} icon={CalendarCheck} color={CY} />
        <Stat label="Avg / Booking" value={overview.bookings ? fmtMoney(Math.round(overview.revenue / overview.bookings)) : "$0"} />
      </div>
      <h3 className="font-serif text-2xl mb-5">Revenue by Vehicle</h3>
      {byCar.length === 0 ? <Empty text="No revenue yet." /> : (
        <div className="space-y-4">
          {byCar.map((x) => (
            <div key={x.car}>
              <div className="flex justify-between text-sm mb-1.5"><span className="text-white/70">{x.car}</span><span className="text-[#FFD400]">{fmtMoney(x.total)}</span></div>
              <div className="h-2.5 bg-white/8 rounded-full overflow-hidden"><div className="h-full bg-[#FFD400] rounded-full" style={{ width: `${(x.total / max) * 100}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TrafficPanel() {
  const [d, setD] = useState(null);
  useEffect(() => { fetchTraffic().then(setD); }, []);
  if (!d) return <Loading />;
  const maxDay = Math.max(1, ...d.series.map((s) => s.count));
  const maxSrc = Math.max(1, ...d.sources.map((s) => s.count));
  return (
    <div data-testid="panel-traffic">
      <InfoBanner title="Website Traffic" />
      <SectionTitle icon={BarChart3}>Website Traffic</SectionTitle>
      <p className="text-white/50 text-sm -mt-3 mb-8">Every visit to your site is counted here, with where the visitor came from.</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
        <Stat testid="traffic-total" label="Total Visits" value={d.total} color={YL} />
        <Stat testid="traffic-unique" label="Unique Visitors" value={d.unique} />
        <Stat testid="traffic-today" label="Today" value={d.today} color={CY} />
        <Stat testid="traffic-last7" label="Last 7 Days" value={d.last7} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="border border-white/10 bg-[#0B0B0B] rounded-2xl p-6">
          <h3 className="font-medium mb-6 flex items-center gap-2"><BarChart3 size={16} className="text-[#FFD400]" /> Visits — Last 7 Days</h3>
          <div className="flex items-end justify-between gap-2 h-52">
            {d.series.map((s) => (
              <div key={s.date} className="flex-1 flex flex-col items-center justify-end h-full">
                <span className="text-xs text-white/50 mb-1">{s.count}</span>
                <div className="w-full bg-[#FFD400] rounded-t" style={{ height: `${(s.count / maxDay) * 100}%`, minHeight: s.count ? "4px" : "0" }} />
                <span className="text-[10px] text-white/40 mt-2">{s.date}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-white/10 bg-[#0B0B0B] rounded-2xl p-6">
          <h3 className="font-medium mb-6 flex items-center gap-2"><Globe size={16} className="text-[#22D3EE]" /> Where Visitors Came From</h3>
          {d.sources.length === 0 ? <Empty text="No traffic data yet." /> : (
            <div className="space-y-5">
              {d.sources.map((s, i) => (
                <div key={s.source}>
                  <div className="flex justify-between text-sm mb-1.5"><span className="text-white/80">{s.source}</span><span className="text-white/50">{s.count} ({s.pct}%)</span></div>
                  <div className="h-2 bg-white/8 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${s.pct}%`, background: i % 2 ? "#22D3EE" : "#FFD400" }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FleetPanel({ onChanged }) {
  const [cars, setCars] = useState(null);
  const [modalCar, setModalCar] = useState(undefined); // undefined=closed, null=add, obj=edit
  const load = () => fetchAdminCars().then(setCars);
  useEffect(() => { load(); }, []); // eslint-disable-line

  const remove = async (slug) => {
    try { await deleteCar(slug); toast.success("Vehicle removed"); await load(); onChanged?.(); }
    catch { toast.error("Failed to remove"); }
  };
  const toggleAvailable = async (c) => {
    try { await updateCar(c.slug, { available: !(c.available !== false) }); await load(); onChanged?.(); }
    catch { toast.error("Failed to update"); }
  };

  if (!cars) return <Loading />;
  return (
    <div data-testid="panel-fleet">
      <InfoBanner title="Upload photos, set prices and manage the vehicles on your website" />
      <div className="flex items-center justify-between mb-6">
        <SectionTitle icon={Car}>Fleet</SectionTitle>
        <button data-testid="add-car-btn" onClick={() => setModalCar(null)} className="flex items-center gap-2 bg-[#FFD400] text-black rounded-lg px-4 py-2 text-sm font-medium hover:bg-[#e6c000]">
          <Plus size={16} /> Add Vehicle
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cars.map((c) => {
          const available = c.available !== false;
          return (
            <div key={c.slug} data-testid={`fleet-car-${c.slug}`} className="border border-white/10 bg-[#0B0B0B] rounded-xl overflow-hidden">
              <div className="relative aspect-[16/9] overflow-hidden">
                <img src={imgUrl(c.image)} alt={c.name} className="w-full h-full object-cover" />
                <span className={`absolute top-3 left-3 text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border ${available ? "border-emerald-400/40 text-emerald-300 bg-black/70" : "border-white/30 text-white/60 bg-black/70"}`}>
                  {available ? "Available" : "Unavailable"}
                </span>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div><p className="font-medium">{c.name}</p><p className="text-white/40 text-sm">{c.type} · {c.mpg} MPG</p></div>
                  <span className="text-[#FFD400]">{fmtMoney(c.price_per_week)}/wk</span>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-4">
                  <button data-testid={`edit-car-${c.slug}`} onClick={() => setModalCar(c)} className="flex items-center justify-center gap-1.5 border border-white/15 rounded-lg py-2 text-sm hover:border-white/40">
                    <Pencil size={13} /> Edit
                  </button>
                  <button data-testid={`toggle-car-${c.slug}`} onClick={() => toggleAvailable(c)} className="flex items-center justify-center gap-1.5 border border-white/15 rounded-lg py-2 text-sm hover:border-white/40">
                    {available ? <><EyeOff size={13} /> Hide</> : <><Eye size={13} /> Show</>}
                  </button>
                  <button data-testid={`delete-car-${c.slug}`} onClick={() => remove(c.slug)} className="flex items-center justify-center gap-1.5 border border-red-400/30 text-red-300 rounded-lg py-2 text-sm hover:bg-red-400/10">
                    <Trash2 size={13} /> Remove
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {modalCar !== undefined && (
        <CarModal car={modalCar} onClose={() => setModalCar(undefined)} onSaved={async () => { setModalCar(undefined); await load(); onChanged?.(); }} />
      )}
    </div>
  );
}

function CarModal({ car, onClose, onSaved }) {
  const editing = !!car;
  const [f, setF] = useState({
    name: car?.name || "", brand: car?.brand || "", type: car?.type || "Sedan",
    price_per_day: car?.price_per_day ?? "", price_per_week: car?.price_per_week ?? "",
    seats: car?.seats ?? 5, mpg: car?.mpg ?? 30, year: car?.year ?? 2024,
    description: car?.description || "", rideshare: car?.rideshare || ["Uber", "Lyft"],
    available: car?.available !== false,
  });
  const [images, setImages] = useState(car ? [car.image, ...(car.gallery || [])].filter(Boolean) : []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (images.length + files.length > 5) { toast.error("Up to 5 photos per vehicle."); return; }
    setUploading(true);
    try {
      for (const file of files) {
        const url = await uploadImage(file);
        setImages((prev) => [...prev, url]);
      }
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const removeImage = (i) => setImages((prev) => prev.filter((_, idx) => idx !== i));
  const makeMain = (i) => setImages((prev) => [prev[i], ...prev.filter((_, idx) => idx !== i)]);

  const save = async () => {
    if (!f.name || !f.brand || !f.price_per_day || !f.price_per_week) { toast.error("Fill name, brand and both prices."); return; }
    if (images.length === 0) { toast.error("Add at least one photo."); return; }
    setSaving(true);
    const payload = {
      ...f, price_per_day: Number(f.price_per_day), price_per_week: Number(f.price_per_week),
      seats: Number(f.seats), mpg: Number(f.mpg), year: Number(f.year),
      image: images[0], gallery: images.slice(1),
    };
    try {
      if (editing) { await updateCar(car.slug, payload); toast.success("Vehicle updated"); }
      else { await createCar(payload); toast.success("Vehicle added"); }
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="car-modal" className="bg-[#0B0B0B] border border-white/15 rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-serif text-2xl">{editing ? "Edit Vehicle" : "Add Vehicle"}</h3>
          <button onClick={onClose} data-testid="car-modal-close"><X size={20} className="text-white/50" /></button>
        </div>

        {/* Photos */}
        <label className="text-white/50 text-xs uppercase tracking-wider">Photos (up to 5 — first is the cover)</label>
        <div className="grid grid-cols-3 gap-2 mt-2 mb-3">
          {images.map((src, i) => (
            <div key={src} className="relative group aspect-[4/3] rounded-lg overflow-hidden border border-white/15">
              <img src={imgUrl(src)} alt="" className="w-full h-full object-cover" />
              {i === 0 && <span className="absolute bottom-1 left-1 bg-[#FFD400] text-black text-[9px] px-1.5 rounded">COVER</span>}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {i !== 0 && <button onClick={() => makeMain(i)} title="Set as cover" className="text-white text-[10px] border border-white/40 rounded px-1.5 py-0.5">Cover</button>}
                <button onClick={() => removeImage(i)} data-testid={`remove-img-${i}`} className="text-red-300"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          {images.length < 5 && (
            <label data-testid="upload-photo-label" className="aspect-[4/3] rounded-lg border border-dashed border-white/25 flex flex-col items-center justify-center text-white/40 text-xs cursor-pointer hover:border-[#FFD400]/60 hover:text-white/70">
              {uploading ? <Loader2 size={18} className="animate-spin" /> : <><UploadCloud size={18} /><span className="mt-1">Upload</span></>}
              <input data-testid="photo-input" type="file" accept="image/*" multiple onChange={onFiles} className="hidden" />
            </label>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" v={f.name} onc={(v) => set("name", v)} testid="car-name" />
          <Field label="Brand" v={f.brand} onc={(v) => set("brand", v)} testid="car-brand" />
          <div>
            <label className="text-white/50 text-xs uppercase tracking-wider">Type</label>
            <select data-testid="car-type" value={f.type} onChange={(e) => set("type", e.target.value)} className="w-full bg-black border border-white/15 rounded-lg px-3 py-2.5 mt-1.5 text-white">
              {["Economy", "Sedan", "SUV"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <Field label="MPG" type="number" v={f.mpg} onc={(v) => set("mpg", v)} testid="car-mpg" />
          <Field label="Price / day ($)" type="number" v={f.price_per_day} onc={(v) => set("price_per_day", v)} testid="car-day" />
          <Field label="Price / week ($)" type="number" v={f.price_per_week} onc={(v) => set("price_per_week", v)} testid="car-week" />
          <Field label="Seats" type="number" v={f.seats} onc={(v) => set("seats", v)} testid="car-seats" />
          <Field label="Year" type="number" v={f.year} onc={(v) => set("year", v)} testid="car-year" />
        </div>
        <div className="mt-3">
          <label className="text-white/50 text-xs uppercase tracking-wider">Description</label>
          <textarea data-testid="car-desc" value={f.description} onChange={(e) => set("description", e.target.value)} rows={2} className="w-full bg-black border border-white/15 rounded-lg px-3 py-2.5 mt-1.5 text-white text-sm" />
        </div>

        <label className="flex items-center gap-3 mt-4 cursor-pointer">
          <input data-testid="car-available" type="checkbox" checked={f.available} onChange={(e) => set("available", e.target.checked)} className="w-4 h-4 accent-[#FFD400]" />
          <span className="text-sm text-white/80">Available for rent (shown on the website)</span>
        </label>

        <button data-testid="save-car-btn" onClick={save} disabled={saving || uploading} className="mt-6 w-full bg-[#FFD400] text-black font-medium py-3 rounded-lg hover:bg-[#e6c000] disabled:opacity-60 flex items-center justify-center gap-2">
          {saving ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : editing ? "Save Changes" : "Save Vehicle"}
        </button>
      </div>
    </div>
  );
}

const Field = ({ label, v, onc, type = "text", testid, className = "" }) => (
  <div className={className}>
    <label className="text-white/50 text-xs uppercase tracking-wider">{label}</label>
    <input data-testid={testid} type={type} value={v} onChange={(e) => onc(e.target.value)} className="w-full bg-black border border-white/15 rounded-lg px-3 py-2.5 mt-1.5 text-white text-sm focus:outline-none focus:border-[#FFD400]/60" />
  </div>
);

function AvailabilityPanel() {
  const [cars, setCars] = useState(null);
  const [slug, setSlug] = useState("");
  const [schedule, setSchedule] = useState(null);
  const [form, setForm] = useState({ start: "", end: "", reason: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { fetchAdminCars().then((c) => { setCars(c); if (c[0]) setSlug(c[0].slug); }); }, []);
  const load = (s) => fetchCarSchedule(s).then(setSchedule);
  useEffect(() => { if (slug) { setSchedule(null); load(slug); } }, [slug]); // eslint-disable-line

  const add = async () => {
    if (!form.start || !form.end) { toast.error("Pick a start and end date."); return; }
    if (form.end <= form.start) { toast.error("End must be after start."); return; }
    setBusy(true);
    try {
      await addBlock(slug, { start: form.start, end: form.end, reason: form.reason || "Unavailable" });
      toast.success("Dates blocked");
      setForm({ start: "", end: "", reason: "" });
      await load(slug);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to block dates"); }
    finally { setBusy(false); }
  };
  const removeBlock = async (id) => {
    try { await deleteBlock(id); toast.success("Block removed"); await load(slug); }
    catch { toast.error("Failed to remove"); }
  };

  if (!cars) return <Loading />;
  return (
    <div data-testid="panel-availability">
      <InfoBanner title="See what's booked and block dates a car is unavailable (maintenance, personal use, etc.)" />
      <SectionTitle icon={CalendarDays}>Availability</SectionTitle>

      <div className="mb-8 max-w-sm">
        <label className="text-white/50 text-xs uppercase tracking-wider">Vehicle</label>
        <select data-testid="availability-car-select" value={slug} onChange={(e) => setSlug(e.target.value)} className="w-full bg-black border border-white/15 rounded-lg px-3 py-2.5 mt-1.5 text-white">
          {cars.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
      </div>

      {!schedule ? <Loading /> : (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="border border-white/10 bg-[#0B0B0B] rounded-2xl p-6">
            <h3 className="font-medium mb-5 flex items-center gap-2"><CalendarCheck size={16} className="text-[#22D3EE]" /> Upcoming Bookings</h3>
            {schedule.bookings.length === 0 ? <p className="text-white/40 text-sm">No upcoming bookings.</p> : (
              <div className="space-y-3">
                {schedule.bookings.map((b) => (
                  <div key={b.reference} className="flex justify-between text-sm border-b border-white/5 pb-3 last:border-0">
                    <div><p className="text-white/80">{b.customer}</p><p className="text-white/40">{b.reference}</p></div>
                    <span className="text-white/60">{b.start} &rarr; {b.end}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border border-white/10 bg-[#0B0B0B] rounded-2xl p-6">
            <h3 className="font-medium mb-5 flex items-center gap-2"><CalendarOff size={16} className="text-[#FFD400]" /> Blocked Dates</h3>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-white/40 text-[10px] uppercase tracking-wider">From</label>
                <input data-testid="block-start" type="date" value={form.start} onChange={(e) => setForm((s) => ({ ...s, start: e.target.value }))} className="w-full bg-black border border-white/15 rounded-lg px-2 py-2 mt-1 text-white text-sm [color-scheme:dark]" />
              </div>
              <div>
                <label className="text-white/40 text-[10px] uppercase tracking-wider">To</label>
                <input data-testid="block-end" type="date" value={form.end} onChange={(e) => setForm((s) => ({ ...s, end: e.target.value }))} className="w-full bg-black border border-white/15 rounded-lg px-2 py-2 mt-1 text-white text-sm [color-scheme:dark]" />
              </div>
            </div>
            <input data-testid="block-reason" value={form.reason} onChange={(e) => setForm((s) => ({ ...s, reason: e.target.value }))} placeholder="Reason (e.g. Maintenance)" className="w-full bg-black border border-white/15 rounded-lg px-3 py-2 mt-2 text-white text-sm" />
            <button data-testid="add-block-btn" onClick={add} disabled={busy} className="w-full mt-3 bg-[#FFD400] text-black rounded-lg py-2.5 text-sm font-medium hover:bg-[#e6c000] disabled:opacity-60">
              {busy ? "Blocking\u2026" : "Block These Dates"}
            </button>
            <div className="mt-5 space-y-2">
              {schedule.blocks.length === 0 ? <p className="text-white/40 text-sm">No blocked dates.</p> : schedule.blocks.map((b) => (
                <div key={b.id} data-testid={`block-${b.id}`} className="flex items-center justify-between text-sm border border-white/10 rounded-lg px-3 py-2">
                  <span className="text-white/70">{b.start} &rarr; {b.end} <span className="text-white/40">&middot; {b.reason}</span></span>
                  <button data-testid={`remove-block-${b.id}`} onClick={() => removeBlock(b.id)} className="text-red-300 hover:text-red-200"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SystemPanel() {
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!f.current || !f.next) { toast.error("Fill in all fields."); return; }
    if (f.next.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (f.next !== f.confirm) { toast.error("New passwords don't match."); return; }
    setBusy(true);
    try {
      await changePassword(f.current, f.next);
      toast.success("Password updated");
      setF({ current: "", next: "", confirm: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update password"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="panel-system">
      <InfoBanner title="Account & security settings" />
      <SectionTitle icon={Cpu}>System</SectionTitle>
      <div className="max-w-md border border-white/10 bg-[#0B0B0B] rounded-2xl p-6">
        <h3 className="font-medium mb-5 flex items-center gap-2"><KeyRound size={16} className="text-[#FFD400]" /> Change Admin Password</h3>
        <label className="text-white/50 text-xs uppercase tracking-wider">Current password</label>
        <input data-testid="current-password" type="password" value={f.current} onChange={(e) => set("current", e.target.value)} className="w-full bg-black border border-white/15 rounded-lg px-3 py-2.5 mt-1.5 mb-4 text-white" />
        <label className="text-white/50 text-xs uppercase tracking-wider">New password</label>
        <input data-testid="new-password" type="password" value={f.next} onChange={(e) => set("next", e.target.value)} className="w-full bg-black border border-white/15 rounded-lg px-3 py-2.5 mt-1.5 mb-4 text-white" />
        <label className="text-white/50 text-xs uppercase tracking-wider">Confirm new password</label>
        <input data-testid="confirm-password" type="password" value={f.confirm} onChange={(e) => set("confirm", e.target.value)} className="w-full bg-black border border-white/15 rounded-lg px-3 py-2.5 mt-1.5 mb-6 text-white" />
        <button data-testid="change-password-btn" onClick={submit} disabled={busy} className="w-full bg-[#FFD400] text-black font-medium py-3 rounded-lg hover:bg-[#e6c000] disabled:opacity-60 flex items-center justify-center gap-2">
          <Lock size={15} /> {busy ? "Updating\u2026" : "Update Password"}
        </button>
      </div>
    </div>
  );
}


const ComingSoon = ({ tab }) => (
  <div data-testid="panel-coming-soon" className="border border-white/10 bg-[#0B0B0B] rounded-2xl p-16 text-center">
    <Cpu size={40} className="text-white/20 mx-auto mb-5" />
    <h2 className="font-serif text-3xl capitalize">{tab.replace("-", " ")}</h2>
    <p className="text-white/40 mt-3 max-w-md mx-auto">This module is on the roadmap. The core tools — Bookings, Cancelled, Agreements, Fleet, Customers, Money and Traffic — are fully live now.</p>
  </div>
);

const Loading = () => <div className="flex items-center justify-center py-24 text-white/40"><Loader2 className="animate-spin mr-2" size={18} /> Loading…</div>;
const Empty = ({ text }) => <div className="border border-white/10 rounded-2xl py-16 text-center text-white/40">{text}</div>;
